import { Injectable, computed, signal } from '@angular/core';

/**
 * How long a request may run before the bar appears. Anything that finishes inside this window
 * never shows it at all — a bar that flashes for 40ms reads as a glitch, not as progress.
 * Exported so the spec can reason about the boundary instead of hard-coding a magic number.
 */
export const LOADING_BAR_DEBOUNCE_MS = 150;

/**
 * Reference-counted "something is in flight" state behind the global loading bar.
 *
 * <p>A counter rather than a boolean because requests overlap: a page that fires three reads at
 * once must keep the bar up until the *last* one lands, and a boolean would be switched off by
 * the first. {@link loadingInterceptor} is the only expected caller, pairing every `begin()` with
 * an `end()` in `finalize()`.
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  private readonly count = signal(0);
  private readonly blockingCount = signal(0);
  private readonly show = signal(false);
  private timer: ReturnType<typeof setTimeout> | null = null;

  /** True once something has been in flight for longer than {@link LOADING_BAR_DEBOUNCE_MS}. */
  readonly visible = this.show.asReadonly();

  /** Number of requests currently in flight — exposed for tests and debugging. */
  readonly inFlight = this.count.asReadonly();

  /**
   * True while a request the user must wait out is running. Counted separately from the bar and
   * **not** debounced: the whole point is that the very next click lands on the overlay rather
   * than on the ยื่นคำขอ button, and 150ms is more than enough for a second click.
   */
  readonly blocking = computed(() => this.blockingCount() > 0);

  /** A request started. On the 0 -> 1 transition, arm the debounce that reveals the bar. */
  begin(blocking = false): void {
    if (blocking) {
      this.blockingCount.set(this.blockingCount() + 1);
    }

    const next = this.count() + 1;
    this.count.set(next);

    if (next === 1 && this.timer === null) {
      this.timer = setTimeout(() => {
        this.timer = null;
        this.show.set(true);
      }, LOADING_BAR_DEBOUNCE_MS);
    }
  }

  /**
   * A request settled — success, failure or cancellation alike. The `Math.max` floor means an
   * unbalanced `end()` can never drive the counter negative, which would otherwise leave the bar
   * pinned on screen for the rest of the session — and, for the blocking counter, the whole
   * portal unusable until reload.
   */
  end(blocking = false): void {
    if (blocking) {
      this.blockingCount.set(Math.max(0, this.blockingCount() - 1));
    }

    const next = Math.max(0, this.count() - 1);
    this.count.set(next);

    if (next === 0) {
      this.clearTimer();
      this.show.set(false);
    }
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
